import { useState, useEffect, useCallback } from "react";
import { listBookQuotes, addQuote, updateQuote, deleteQuote, toggleQuoteLike, QUOTE_MAX } from "../api/quotes";

/**
 * 책에서 옮긴 문장.
 *
 * 프로필 수정 안에만 두었더니 아무도 못 찾았다. 문장은 읽다가 옮기는
 * 것이지 나중에 설정 화면에서 떠올리는 게 아니라, 책 옆에 둔다.
 *
 * 하나도 없을 때는 목록을 그리지 않는다 — 책 화면은 이미 길다.
 */
/**
 * @param {"mine"|"public"} mode
 *   mine   — 내 문장 + 옮기기 (문장과 기록 탭, 내 책일 때만)
 *   public — 공개된 문장 전부 (이야기 나누기 탭, 누구나)
 */
export default function BookQuotes({ book, currentUser, users = [], mode = "mine", focusId, clearFocus }) {
  const uid = currentUser?.uid;
  const isGuest = !currentUser || currentUser.isAnonymous;

  const [quotes, setQuotes] = useState([]);
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState("");
  const [page, setPage] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [saving, setSaving] = useState(false);
  /** 고치는 중인 문장 id. 이야기(메모)와 같은 방식이다 — 그 줄이 입력칸이 된다. */
  const [editing, setEditing] = useState(null);
  const [eText, setEText] = useState("");
  const [ePage, setEPage] = useState("");
  const [ePublic, setEPublic] = useState(true);
  /** 기본은 접어둔다. 문장이 길면 한두 개만으로도 기록 입력칸이
      화면 밖으로 밀려난다 — 여기 와서 대개 하려는 일은 쓰는 것이다. */
  const [open, setOpen] = useState(false);
  /** 다 불러왔는지. 못 찾은 문장인지 아직 안 온 것인지 가른다. */
  const [loaded, setLoaded] = useState(false);
  /** 방금 데려온 문장. 잠깐 밝혀 어느 것인지 알게 한다. */
  const [flash, setFlash] = useState(null);

  const reload = useCallback(async () => {
    if (isGuest || !book?.id) return;
    try {
      const all = await listBookQuotes(book.id);
      // '문장과 기록' 은 내 자리다. 남의 문장은 '이야기 나누기' 에서 본다.
      setQuotes(mode === "mine" ? all.filter((q) => q.userId === uid) : all.filter((q) => q.isPublic));
      setLoaded(true);
    } catch (e) {
      console.error("문장 로드 실패:", e.message);
    }
  }, [book?.id, isGuest, mode, uid]);

  useEffect(() => { reload(); }, [reload]);

  /* 우주의 이야기에서 문장을 눌러 들어오면 그 문장까지 데려간다.
     목록은 기본이 접힘이라 함께 편다. 그새 비공개로 돌렸다면 못 찾는데,
     그때는 조용히 놓아준다 — 남겨두면 이 책을 열 때마다 끌려간다. */
  useEffect(() => {
    const want = String(focusId ?? "");
    if (!want.startsWith("q:") || !loaded) return;
    const id = want.slice(2);
    clearFocus?.();
    if (!quotes.some((q) => String(q.id) === id)) return;
    setOpen(true);
    setFlash(id);
  }, [focusId, loaded, quotes, clearFocus]);

  /* 스크롤·밝힘은 flash 에 묶는다. focusId 에 묶으면 clearFocus() 가 곧바로
     이펙트를 다시 돌려 예약해 둔 일을 취소한다. 목록이 막 펴진 참이라
     아직 안 그려졌을 수 있어 한 번 더 맞춘다. */
  useEffect(() => {
    if (!flash) return undefined;
    const place = () => {
      const el = document.querySelector(`[data-quote-id="${CSS.escape(String(flash))}"]`);
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const h = el.offsetHeight;
      const y = h < window.innerHeight * 0.7
        ? window.scrollY + top - (window.innerHeight - h) / 2
        : window.scrollY + top - 90;
      window.scrollTo(0, Math.max(0, y));
    };
    const raf = requestAnimationFrame(place);
    const again = setTimeout(place, 320);
    const off = setTimeout(() => setFlash(null), 2400);
    return () => { cancelAnimationFrame(raf); clearTimeout(again); clearTimeout(off); };
  }, [flash]);

  if (isGuest) return null;

  const nameOf = (id) =>
    id === uid ? "나" : (users.find((u) => u.id === id)?.name ?? "누군가");

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await addQuote({
        userId: uid,
        bookId: book.id,
        text,
        page: page ? Number(page) : null,
        isPublic,
      });
      setText(""); setPage(""); setIsPublic(true); setWriting(false);
      await reload();
    } catch (e) {
      console.error("문장 저장 실패:", e.message);
    }
    setSaving(false);
  };

  const startEdit = (q) => {
    setEditing(q.id);
    setEText(q.text);
    setEPage(q.page ? String(q.page) : "");
    setEPublic(q.isPublic);
  };

  /* 그 한 행만 고친다. 목록을 통째로 다시 쓰지 않으므로 같은 책을 동시에
     보고 있어도 서로의 문장을 덮어쓰지 않는다. */
  const saveEdit = async (id) => {
    if (!eText.trim()) return;
    setSaving(true);
    try {
      await updateQuote(id, {
        text: eText,
        page: ePage ? Number(ePage) : null,
        isPublic: ePublic,
      });
      setEditing(null);
      await reload();
    } catch (e) {
      console.error("문장 수정 실패:", e.message);
    }
    setSaving(false);
  };

  return (
    <div className="bq">
      {/* 버튼은 맨 위에 고정한다. 목록 아래에 두면 문장이 쌓일수록 멀어진다. */}
      {mode === "public" ? null : writing ? (
        <div className="bq-form">
          <textarea
            className="bq-input"
            placeholder="기억에 남은 문장을 옮겨 적어보세요"
            value={text}
            maxLength={QUOTE_MAX}
            rows={3}
            autoFocus
            onChange={(e) => setText(e.target.value)}
          />
          <div className="bq-row">
            <input
              className="bq-page"
              type="number"
              inputMode="numeric"
              placeholder="쪽수"
              value={page}
              min="1"
              onChange={(e) => setPage(e.target.value)}
            />
            <span className={text.length >= QUOTE_MAX ? "bq-count full" : "bq-count"}>
              {text.length} / {QUOTE_MAX}
            </span>
          </div>
          {/* 메모의 공개 체크박스와 같은 문구를 쓴다. 같은 뜻인데
              다르게 쓰면 어느 쪽이 무엇인지 헷갈린다. */}
          <label className="bq-check">
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
            <span>다른 독자에게도 보이기</span>
          </label>
          <div className="bq-btns">
            <button className="bq-save" onClick={save} disabled={!text.trim() || saving}>
              {saving ? "저장 중…" : "저장"}
            </button>
            <button className="bq-cancel" onClick={() => { setWriting(false); setText(""); setPage(""); }}>
              취소
            </button>
          </div>
        </div>
      ) : (
        <div className="bq-addwrap">
          <button className="bq-add" onClick={() => setWriting(true)}>+ 문장 옮기기</button>
        </div>
      )}

      {quotes.length > 0 && (
        <button
          className={`bq-label bq-toggle${open ? " open" : ""}`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {mode === "public" ? "독자들이 옮긴 문장" : "이 책에서 옮긴 문장"}
          <span className="bq-n">{quotes.length}</span>
          <span className="bq-caret" aria-hidden="true">{open ? "▴" : "▾"}</span>
        </button>
      )}

      {open && quotes.map((q) => (
        <div key={q.id} data-quote-id={q.id} className={`bq-item${String(flash) === String(q.id) ? " bq-flash" : ""}`}>
          {editing === q.id ? (
            /* 쓰던 폼을 그대로 다시 쓴다. 고치는 화면만 다르게 생기면
               같은 일을 두 가지 모양으로 배우게 된다. */
            <div className="bq-form">
              <textarea
                className="bq-input"
                value={eText}
                maxLength={QUOTE_MAX}
                rows={3}
                autoFocus
                onChange={(e) => setEText(e.target.value)}
              />
              <div className="bq-row">
                <input
                  className="bq-page"
                  type="number"
                  inputMode="numeric"
                  placeholder="쪽수"
                  value={ePage}
                  min="1"
                  onChange={(e) => setEPage(e.target.value)}
                />
                <span className={eText.length >= QUOTE_MAX ? "bq-count full" : "bq-count"}>
                  {eText.length} / {QUOTE_MAX}
                </span>
              </div>
              <label className="bq-check">
                <input type="checkbox" checked={ePublic} onChange={(e) => setEPublic(e.target.checked)} />
                <span>다른 독자에게도 보이기</span>
              </label>
              <div className="bq-btns">
                <button className="bq-save" onClick={() => saveEdit(q.id)} disabled={!eText.trim() || saving}>
                  {saving ? "저장 중…" : "수정 완료"}
                </button>
                <button className="bq-cancel" onClick={() => setEditing(null)}>취소</button>
              </div>
            </div>
          ) : (
          <>
          <span className="bq-mark" aria-hidden="true">“</span>
          <div className="bq-body">
            <div className="bq-text">{q.text}</div>
            <div className="bq-meta">
              <span>{nameOf(q.userId)}</span>
              {q.page && <span>p.{q.page}</span>}
              {!q.isPublic && <span className="bq-priv">비공개</span>}
              {/* 비공개 문장에는 좋아요를 두지 않는다. 나만 보는 글이라
                  누를 사람도 셀 것도 없다. */}
              {q.isPublic && (() => {
                const liked = (q.likes ?? []).includes(uid);
                return (
                  <button
                    className={`bq-like${liked ? " on" : ""}`}
                    aria-pressed={liked}
                    aria-label={liked ? "좋아요 취소" : "좋아요"}
                    onClick={async () => {
                      /* 눌린 것부터 보여주고 저장한다. 왕복을 기다리면
                         두 번 눌러 두 번 저장되는 일이 생긴다. */
                      setQuotes((prev) => prev.map((x) => x.id !== q.id ? x : {
                        ...x,
                        likes: liked ? x.likes.filter((v) => v !== uid) : [...(x.likes ?? []), uid],
                      }));
                      try {
                        await toggleQuoteLike(q.id, uid, liked);
                      } catch (e) {
                        console.error("좋아요 실패:", e.message);
                        reload();
                      }
                    }}
                  >
                    {liked ? "♥" : "♡"}
                    {(q.likes?.length ?? 0) > 0 && <span className="bq-like-n">{q.likes.length}</span>}
                  </button>
                );
              })()}
              {q.userId === uid && (
                /* 둘을 한 덩어리로 묶는다. 따로 두면 좁은 폭에서 '지우기' 만
                   다음 줄로 떨어져, 무엇에 딸린 버튼인지 흐려진다. */
                <span className="bq-acts">
                  {/* 옮겨 적다 보면 오타가 나고, 쪽수를 잘못 적기도 한다.
                      지우고 다시 쓰게 하면 좋아요도 함께 사라진다. */}
                  <button className="bq-edit" onClick={() => startEdit(q)}>고치기</button>
                  <button
                    className="bq-del"
                    onClick={async () => { await deleteQuote(q.id); reload(); }}
                  >지우기</button>
                </span>
              )}
            </div>
          </div>
          </>
          )}
        </div>
      ))}
    </div>
  );
}
